import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { FieldError } from "@/components/FieldError";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";

export default function Clients() {
  const { isOrgAdmin } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/revenue/clients");
      setClients(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (c) => {
    try {
      await api.patch(`/revenue/clients/${c.id}`, { is_active: !c.is_active });
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      <PageHeader title="Clients" description="Client master data used for RA bill generation and receivables." action={isOrgAdmin && <CreateClientDialog onCreated={load} />} />
      <div className="rounded-xl border border-border bg-card">
        {!loading && clients.length === 0 ? (
          <EmptyState title="No clients yet" testId="clients-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>GSTIN</TableHead><TableHead>Retention %</TableHead><TableHead>TDS %</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {clients.map((c) => (
                <TableRow key={c.id} data-testid={`client-row-${c.code}`}>
                  <TableCell className="font-mono text-xs text-foreground">{c.code}</TableCell>
                  <TableCell className="text-foreground">{c.name}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{c.gstin || "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{c.default_retention_pct}%</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{c.default_tds_pct}%</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch checked={c.is_active} disabled={!isOrgAdmin} onCheckedChange={() => toggleActive(c)} data-testid={`client-active-toggle-${c.code}`} />
                      <Badge variant={c.is_active ? "default" : "secondary"}>{c.is_active ? "Active" : "Inactive"}</Badge>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateClientDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", gstin: "", billing_address: "", contact_person: "", phone: "", email: "", default_retention_pct: "0", default_tds_pct: "0" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      await api.post("/revenue/clients", form);
      toast.success(`Client ${form.code} created`);
      setForm({ code: "", name: "", gstin: "", billing_address: "", contact_person: "", phone: "", email: "", default_retention_pct: "0", default_tds_pct: "0" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      setErrors(getFieldErrors(err));
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-client-button"><Plus className="h-4 w-4" /> New Client</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New Client</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-client-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code</Label><Input required data-testid="client-code-input" value={form.code} onChange={set("code")} className="mt-1.5" /><FieldError error={errors.code} /></div>
            <div><Label>Name</Label><Input required data-testid="client-name-input" value={form.name} onChange={set("name")} className="mt-1.5" /><FieldError error={errors.name} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>GSTIN</Label><Input data-testid="client-gstin-input" value={form.gstin} onChange={set("gstin")} className="mt-1.5" /></div>
            <div><Label>Contact person</Label><Input data-testid="client-contact-input" value={form.contact_person} onChange={set("contact_person")} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Billing address</Label><Input data-testid="client-address-input" value={form.billing_address} onChange={set("billing_address")} className="mt-1.5" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Phone</Label><Input data-testid="client-phone-input" value={form.phone} onChange={set("phone")} className="mt-1.5" /></div>
            <div><Label>Email</Label><Input data-testid="client-email-input" value={form.email} onChange={set("email")} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Default retention %</Label><Input type="number" step="0.01" data-testid="client-retention-input" value={form.default_retention_pct} onChange={set("default_retention_pct")} className="mt-1.5" /></div>
            <div><Label>Default TDS %</Label><Input type="number" step="0.01" data-testid="client-tds-input" value={form.default_tds_pct} onChange={set("default_tds_pct")} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-client-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
